/**
 * Report editor — a Canva/Gamma-style deck builder on the shared slide design
 * system (SlideRenderer renders; the backend exports PDF/PPTX).
 *
 * Editing happens ON the slide: click any text to change it, hover media for
 * Replace/Remove, use the "+" affordances to add bullets, cards, stats or an
 * image, and the "×" on a card to remove it. The side panel keeps only what
 * has no place on the canvas: Build with Kai, page type/background/layout,
 * speaker notes and the deck-level Brand & logo.
 *
 * The working copy lives here; edits autosave (debounced) via PATCH.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ArrowLeft, Plus, Trash2, Copy, ChevronUp, ChevronDown, Sparkles, Loader2, Play,
  Download, FileText, ImagePlus, X, Check, Undo2, Upload, ChevronRight, Phone, Ear, Volume2, VolumeX, Mic, Wand2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { VisibilityControl, type VisibilityValue } from '@/cowork/omniad/components/VisibilityControl';
import { ShareLinkControl } from '@/cowork/dashboards/ShareLinkControl';
import type { VisibilityInfo } from '@/cowork/omniad/api/coworkApi';
import { coworkApi, API_BASE_URL, type DeckSettings, type CreatorFacts, type TeamMember } from '@/cowork/omniad/api/coworkApi';
import { LykkePresenter } from './LykkePresenter';
import { PresentationModal } from '@/cowork/omniad/components/PresentationModal';
import { useVideosMuted } from '@/cowork/omniad/components/slides/videoPrefs';
import { SlideRenderer, SlideEditProvider, deriveTheme, type Theme, type SlideEditApi, CREATOR_FIELDS, CREATOR_VARIANTS, creatorFieldsOf } from '@/cowork/omniad/components/slides/SlideRenderer';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DeckGuardrails } from './DeckGuardrails';
import { DeckCopilot } from './DeckCopilot';
import { SlideThumb } from './SlideThumb';
import { ImportPagesDialog, type ImportSource } from './import/ImportPagesDialog';
import { isGhost, reshapeGhost } from './ghostPages';
import { ElementToolbox, ElementInspector, AlignBar, ChartPageEditor } from './ElementToolbox';
import { ElementSelection } from '@/cowork/omniad/components/slides/SlideRenderer';
import { FONT_CHOICES, captureAsElements } from '@/cowork/omniad/components/slides/elements';
import { useDeckFonts } from '@/cowork/omniad/components/slides/deckFonts';
import { ContentHubPicker, prefetchContentHubPicker, type SlideMedia } from './ContentHubPicker';
import { LAYOUT_CATALOG, LAYOUT_CATEGORIES, BASE_TYPES, type LayoutEntry } from './layouts';
import { retype, matchLayout } from './retype';

type Slide = Record<string, any>;

const THEMES = ['light', 'soft', 'dark', 'brand'] as const;
const LAYOUTS = ['left', 'center', 'split-left', 'split-right', 'image-left', 'image-right', 'accent-left', 'accent-right', 'accent-top'] as const;

const KAI_EXAMPLES = [
  'Present my top 3 creators by CTR — their video, CTR level and follower count',
  'Total impressions, reach and interactions for this campaign as KPIs',
  'Budget vs spend per campaign as a bar chart',
  'Three takeaways from the collaboration metrics, as bullets',
];

/** Mirror of the backend's applyDeckSettings so the canvas updates instantly. */
function applySettings(brand: Record<string, any>, s: DeckSettings): Record<string, any> {
  const out: any = { ...brand, brand: { ...(brand.brand || {}) } };
  out.brand.logos = { ...(out.brand.logos || {}) };
  if (s.showLogo === false) {
    out.brand.logos = {}; delete out.logos; delete out.brand.logoUrl; delete out.brand.logoDark; delete out.brand.logoLight; delete out.brand_logo_url;
  } else if (s.logoUrl) {
    const u = absolutize(s.logoUrl);
    out.brand.logos = { logoDark: u, logoLight: u, iconDark: u, iconLight: u };
    out.logos = out.brand.logos;
  }
  out.beatlyMark = s.beatlyMark !== false;
  if (s.brandName) out.brand.name = s.brandName;
  if (s.primaryColor) out.brand.primaryColors = [s.primaryColor, ...((out.brand.primaryColors || []).slice(1))];
  if (s.accentColor) { const cur: string[] = out.brand.primaryColors || []; out.brand.primaryColors = [cur[0] || s.accentColor, s.accentColor, ...cur.slice(2)]; }
  if (typeof s.fontFamily === 'string' && s.fontFamily) { out.brand.fonts = [s.fontFamily]; out.fonts = [s.fontFamily]; }
  if (Array.isArray(s.palette) && s.palette.length) out.palette = s.palette;
  const hex = (v?: string | null) => (v ? (v.startsWith('#') ? v : `#${v}`) : undefined);
  if (s.backgroundColor || s.textColor) out.deckColors = { background: hex(s.backgroundColor), text: hex(s.textColor) };
  return out;
}
function absolutize(url: string): string {
  return url.startsWith('/assets/') ? `${API_BASE_URL}${url}` : url;
}

const inputCls = 'w-full rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[13px] text-[#101828] focus:border-[#be43ff] focus:outline-none';
const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#667085]';
const btnCls = 'inline-flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[12px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:opacity-50';
const btnPrimaryCls = 'inline-flex items-center gap-1.5 rounded-lg bg-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50';

/** Pick a Beatly person for the team page: the employee directory (staff login) or the team's account manager (client login), or a blank card. */
function TeamMemberPicker({ onPick, onClose }: { onPick: (m: TeamMember | null) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const list = useQuery({ queryKey: ['cowork', 'team-members'], queryFn: () => coworkApi.presentations.teamMembers(), staleTime: 10 * 60_000 });
  const members = (list.data?.members || []).filter((m) => !q.trim() || `${m.name} ${m.title || ''} ${m.email || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const abs = (u: string | null) => (u && u.startsWith('/') ? `${API_BASE_URL}${u}` : u);
  useEffect(() => { const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }; window.addEventListener('keydown', onKey, true); return () => window.removeEventListener('keydown', onKey, true); }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" onClick={onClose} className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-6">
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-[#eaecf0] px-5 py-3">
          <div className="text-[15px] font-semibold text-[#101828]">Add a team member</div>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or role…" className="ml-auto w-64 rounded-lg border border-[#d0d5dd] px-3 py-1.5 text-[13px]" />
          <button onClick={onClose} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-3 text-[12px] text-[#667085]">{list.data?.mode === 'employees' ? 'Beatly employee directory — name, role and photo come straight from the back office.' : 'Your team on the platform. Log in as Beatly staff to pick from the whole employee directory.'}</div>
          {list.isPending && <div className="flex items-center gap-2 py-6 text-[12px] text-[#98a2b3]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading people…</div>}
          {list.isError && <div className="py-4 text-[12px] text-[#b42318]">Could not load the team list.</div>}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            {members.map((m) => (
              <button key={m.id} type="button" onClick={() => onPick({ ...m, pictureUrl: abs(m.pictureUrl) })} className="flex items-center gap-3 rounded-xl border border-[#eaecf0] p-2.5 text-left hover:border-[#be43ff] hover:bg-[#faf5ff]">
                {m.pictureUrl ? <img src={abs(m.pictureUrl) as string} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" /> : <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#e9d5ff] text-[14px] font-bold text-[#7c3aed]">{m.name.charAt(0)}</span>}
                <span className="min-w-0"><span className="block truncate text-[13px] font-semibold text-[#101828]">{m.name}</span><span className="block truncate text-[11.5px] text-[#667085]">{m.title || (m.source === 'campaign-managers' ? 'Campaign manager' : '')}</span></span>
              </button>
            ))}
            <button type="button" onClick={() => onPick(null)} className="flex items-center gap-3 rounded-xl border border-dashed border-[#d0d5dd] p-2.5 text-left text-[13px] font-semibold text-[#7c3aed] hover:border-[#be43ff] hover:bg-[#faf5ff]"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#f4ecff]">+</span>Add by hand<span className="block text-[11px] font-normal text-[#98a2b3]">Type the name and role, pick a photo</span></button>
          </div>
          {!list.isPending && !members.length && <div className="py-4 text-[12px] text-[#98a2b3]">Nobody matches — add the person by hand.</div>}
        </div>
      </div>
    </div>
  );
}


/** Page-type control in the inspector: the current type as a miniature, and the same catalogue as Add page to switch. */
function PageTypePicker({ slide, theme, title, onPick }: { slide: Slide; theme: Theme; title: string; onPick: (entry: LayoutEntry) => void }) {
  const [open, setOpen] = useState(false);
  const match = matchLayout(slide);
  const label = match?.label || BASE_TYPES.find((t) => t.id === (slide.type || 'content'))?.label || 'Page';
  const baseType = slide.type || 'content';
  const siblings = LAYOUT_CATALOG.filter((l) => l.id !== match?.id && (l.make().type || 'content') === baseType && (baseType !== 'chart' || !l.make().chart?.type || l.make().chart.type === slide.chart?.type || ['text-left', 'text-right', 'image-left', 'image-right'].includes(l.make().layout))).slice(0, 10);
  return (
    <div>
      <button onClick={() => setOpen(true)} className="flex w-full items-center gap-2.5 rounded-lg border border-[#d0d5dd] p-1.5 text-left hover:border-[#be43ff] hover:bg-[#fcf7ff] focus:border-[#be43ff] focus:outline-none" title="Change the page type">
        <div className="w-[4.5rem] shrink-0 overflow-hidden rounded-md border border-[#eaecf0]">
          <SlideThumb slide={match ? match.preview() : slide} index={slide.type === 'title' ? 0 : 1} theme={theme} title={title} radius={4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-[#101828]">{label}</div>
          <div className="truncate text-[10px] text-[#98a2b3]">{match?.category || 'Click to change'}</div>
        </div>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#667085]" />
      </button>
      {/* Variations: the other layouts that hold this page's kind of content, one click away (Beautiful.ai's layout strip). */}
      {siblings.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#98a2b3]">Variations</div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {siblings.map((l) => (
              <button key={l.id} onClick={() => onPick(l)} title={l.label} className="w-[4.2rem] shrink-0 overflow-hidden rounded-md border border-[#eaecf0] hover:border-[#be43ff]">
                <SlideThumb slide={l.preview()} index={l.id === 'cover' ? 0 : 1} theme={theme} title={title} radius={3} />
              </button>
            ))}
          </div>
        </div>
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed right-[21rem] top-16 z-50 max-h-[82vh] w-[44rem] overflow-y-auto rounded-xl border border-[#eaecf0] bg-white p-3 shadow-xl">
            <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
              <div className="text-[12px] font-semibold text-[#101828]">Change page type</div>
              <div className="text-[10px] text-[#98a2b3]">Your title, text and images carry over where the new layout has room for them.</div>
            </div>
            {LAYOUT_CATEGORIES.map((cat) => (
              <div key={cat} className="mb-3">
                <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#667085]">{cat}</div>
                <div className="grid grid-cols-4 gap-2">
                  {LAYOUT_CATALOG.filter((l) => l.category === cat).map((l) => {
                    const active = match?.id === l.id;
                    return (
                      <button key={l.id} onClick={() => { onPick(l); setOpen(false); }} className={`group rounded-lg border p-1.5 text-left ${active ? 'border-[#be43ff] bg-[#fcf7ff] ring-1 ring-[#be43ff]' : 'border-[#eaecf0] hover:border-[#be43ff] hover:bg-[#fcf7ff]'}`} title={l.label}>
                        <SlideThumb slide={l.preview()} index={l.id === 'cover' ? 0 : 1} theme={theme} title={title} radius={6} />
                        <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-[#101828]"><span className="truncate">{l.label}</span>{active && <Check className="h-3 w-3 shrink-0 text-[#be43ff]" />}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ReportEditor({ presentationId, onBack }: { presentationId: string; onBack: () => void }) {
  const [title, setTitle] = useState('');
  const [slides, setSlides] = useState<Slide[]>([]);
  const [brand, setBrand] = useState<Record<string, any>>({});
  const [settings, setSettings] = useState<DeckSettings>({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [saveState, setSaveState] = useState<'saved' | 'dirty' | 'saving' | 'error'>('saved');
  const [presenting, setPresenting] = useState(false);
  const [lykkePresenting, setLykkePresenting] = useState<false | 'present' | 'copilot'>(false);
  const [exporting, setExporting] = useState<'pdf' | 'pptx' | null>(null);
  const [pptxMenu, setPptxMenu] = useState(false);
  const [voice, setVoice] = useState(false);
  // A Build click on a ghost page: Lykke's session writes it (and narrates); without her, the editor writes it itself.
  const [buildReq, setBuildReq] = useState<{ at: number | 'all'; n: number } | null>(null);
  const buildGhost = (at: number | 'all') => {
    if (voice) { setBuildReq((r) => ({ at, n: (r?.n || 0) + 1 })); return; }
    setVoice(true); setBuildReq((r) => ({ at, n: (r?.n || 0) + 1 }));
  };
  const ghostCount = slides.filter((s) => isGhost(s)).length;
  const [selectedEl, setSelectedElRaw] = useState<string | null>(null);
  const [beautifyOpen, setBeautifyOpen] = useState(false);
  const [beautifying, setBeautifying] = useState<string | null>(null);
  const [restyleBrief, setRestyleBrief] = useState('');
  const stageRef = useRef<HTMLDivElement>(null);
  /** Take the current template page over as a free canvas: its rendered content becomes elements where it is drawn. */
  const takeOver = () => {
    const sl = slidesRef.current[index]; if (!sl || sl.type === 'free') return;
    const elements = captureAsElements(stageRef.current, sl);
    if (!elements.length) { toast.error('Nothing to take over on this page'); return; }
    const next = slidesRef.current.slice();
    next[index] = { type: 'free', theme: sl.theme, bgColor: sl.bgColor, textColor: sl.textColor, notes: sl.notes, footnote: sl.footnote, elements, _from: { type: sl.type, variant: sl.variant } };
    commit(next, index);
    toast.success('This page is now Free Style — everything on it can be moved. Undo brings the template back.', { duration: 6000 });
  };
  const [multiEl, setMultiEl] = useState<string[]>([]);
  /** Click selects one; shift-click adds to or removes from the selection. */
  const setSelectedEl = useCallback((id: string | null, add?: boolean) => {
    if (!id) { setSelectedElRaw(null); setMultiEl([]); return; }
    if (add) { setMultiEl((m) => { const base = m.length ? m : (selectedEl ? [selectedEl] : []); return base.includes(id) ? base.filter((x) => x !== id) : [...base, id]; }); setSelectedElRaw(id); return; }
    setSelectedElRaw(id); setMultiEl([]);
  }, [selectedEl]);
  const [videosMuted, setVideosMuted] = useVideosMuted();
  const [kaiPrompt, setKaiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [pendingLabel, setPendingLabel] = useState('');
  const [picker, setPicker] = useState<{ apply: (m: SlideMedia) => void; creator?: (f: CreatorFacts) => void } | null>(null);
  const [addMenu, setAddMenu] = useState(false);
  const [importFrom, setImportFrom] = useState<ImportSource | null>(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const undoStack = useRef<Array<{ slides: Slide[]; index: number }>>([]);
  const dirtyRef = useRef(false);
  const slidesRef = useRef<Slide[]>([]);
  slidesRef.current = slides;
  const logoInput = useRef<HTMLInputElement>(null);

  const theme: Theme = useMemo(() => deriveTheme(applySettings(brand, settings)), [brand, settings]);
  const current = slides[index];

  const [vis, setVis] = useState<VisibilityValue & { isOwner: boolean; unowned?: boolean }>({ visibility: 'team', allowedUserIds: [], isOwner: true });
  const [share, setShare] = useState<{ shareEnabled: boolean; shareToken: string | null }>({ shareEnabled: false, shareToken: null });
  const [savingVis, setSavingVis] = useState(false);
  const changeVisibility = async (v: VisibilityValue) => {
    setSavingVis(true);
    try {
      const r = await coworkApi.presentations.update(presentationId, { visibility: v.visibility, allowedUserIds: v.allowedUserIds });
      setVis({ visibility: r.visibility || v.visibility, allowedUserIds: r.allowedUserIds || v.allowedUserIds, isOwner: r.isOwner ?? true, unowned: (r as { unowned?: boolean }).unowned ?? false });
      void queryClient.invalidateQueries({ queryKey: ['cowork', 'presentations'] });
      toast.success(v.visibility === 'private' ? 'Only you can see this report now' : v.visibility === 'members' ? `Shared with ${v.allowedUserIds.length} team member${v.allowedUserIds.length === 1 ? '' : 's'}` : 'Everyone on the team can see this report');
    } catch (e) { toast.error((e as Error).message); } finally { setSavingVis(false); }
  };
  useEffect(() => {
    let cancelled = false;
    coworkApi.presentations.get(presentationId)
      .then((d) => {
        if (cancelled) return;
        setTitle(d.title);
        { const vi = d as Partial<VisibilityInfo>; setVis({ visibility: vi.visibility || 'team', allowedUserIds: vi.allowedUserIds || [], isOwner: vi.isOwner !== false, unowned: vi.unowned === true }); }
        { const sh = d as { shareEnabled?: boolean; shareToken?: string | null }; setShare({ shareEnabled: !!sh.shareEnabled, shareToken: sh.shareToken || null }); }
        setSlides((d.slides as Slide[]) || []);
        setBrand(d.brand || {});
        setSettings(d.settings || {});
        setLoaded(true);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [presentationId]);

  // A deck created with "Build with Lykke" carries its brief: open the copilot at once, it plans from the brief.
  useEffect(() => { if (loaded && settings.pendingBrief) setVoice(true); }, [loaded, settings.pendingBrief]);
  // Text-to-slides: while Kai is building the deck, poll until it lands.
  const deckGenerating = settings.generation?.status === 'running';
  // Warm the Content Hub tree + first page so the picker opens instantly.
  const queryClient = useQueryClient();
  const deckCampaignId = settings.generation?.campaign?.id;
  useEffect(() => { prefetchContentHubPicker(queryClient, deckCampaignId); }, [queryClient, deckCampaignId]);
  useEffect(() => {
    if (!loaded || !deckGenerating) return;
    const t = setInterval(async () => {
      try {
        const d = await coworkApi.presentations.get(presentationId);
        const st = d.settings?.generation?.status;
        if (st === 'done') {
          setTitle(d.title);
          setSlides((d.slides as Slide[]) || []);
          setSettings(d.settings || {});
          setIndex(0);
          toast.success(`Report built — ${(d.slides || []).length} pages`);
        } else if (st === 'failed') {
          setSettings(d.settings || {});
          toast.error(`Kai could not build the report: ${d.settings?.generation?.error || 'unknown error'}`);
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [loaded, deckGenerating, presentationId]);

  // ── Autosave ──
  useEffect(() => {
    if (!loaded || !dirtyRef.current || deckGenerating) return;
    setSaveState('dirty');
    const t = setTimeout(async () => {
      setSaveState('saving');
      try {
        await coworkApi.presentations.update(presentationId, { title: title.trim() || 'Untitled report', slides, settings });
        dirtyRef.current = false;
        setSaveState('saved');
      } catch (e) {
        setSaveState('error');
        toast.error(`Could not save: ${(e as Error).message}`);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [slides, title, settings, loaded, presentationId, deckGenerating]);

  const commit = useCallback((next: Slide[], nextIndex?: number) => {
    undoStack.current.push({ slides: slidesRef.current, index });
    if (undoStack.current.length > 40) undoStack.current.shift();
    dirtyRef.current = true;
    setSlides(next);
    if (nextIndex !== undefined) setIndex(Math.max(0, Math.min(nextIndex, next.length - 1)));
  }, [index]);

  const undo = useCallback(() => {
    const prev = undoStack.current.pop();
    if (!prev) return false;
    dirtyRef.current = true;
    setSlides(prev.slides);
    setIndex(Math.min(prev.index, prev.slides.length - 1));
    return true;
  }, []);

  /** Field-level edit of the current slide (from the canvas or the panel). */
  const updateCurrent = useCallback((patch: Partial<Slide> | ((s: Slide) => Slide)) => {
    const cur = slidesRef.current[index];
    if (!cur) return;
    const nextSlide = typeof patch === 'function' ? patch(cur) : { ...cur, ...patch };
    const next = slidesRef.current.slice();
    next[index] = nextSlide;
    undoStack.current.push({ slides: slidesRef.current, index });
    if (undoStack.current.length > 40) undoStack.current.shift();
    dirtyRef.current = true;
    setSlides(next);
  }, [index]);

  const pickMedia = useCallback((apply: (m: SlideMedia) => void) => setPicker({ apply }), []);
  const pickCreator = useCallback((apply: (f: CreatorFacts) => void) => setPicker({ apply: () => {}, creator: apply }), []);
  const [teamPick, setTeamPick] = useState<((m: TeamMember | null) => void) | null>(null);
  const pickTeamMember = useCallback((apply: (m: TeamMember | null) => void) => setTeamPick(() => apply), []);

  const editApi: SlideEditApi = useMemo(() => ({
    editable: true,
    update: (mutate) => updateCurrent((s) => mutate({ ...s })),
    pickMedia,
    pickCreator,
    pickTeamMember,
    palette: [theme.primary, theme.accent, theme.text, theme.textMuted, ...(theme.palette || [])],
  }), [updateCurrent, pickMedia, pickCreator, pickTeamMember, theme]);

  const addSlide = (slide: Slide) => {
    const next = slides.slice();
    next.splice(index + 1, 0, slide);
    commit(next, index + 1);
    setAddMenu(false);
  };
  /** Pages copied from another report or imported from a file, after the current page. */
  const insertSlides = (list: Slide[], from: string) => {
    const next = slides.slice();
    next.splice(index + 1, 0, ...list);
    commit(next, index + 1);
    setImportFrom(null);
    toast.success(`Added ${list.length} page${list.length === 1 ? '' : 's'} from ${from}`);
  };
  const duplicateSlide = () => { if (!current) return; const next = slides.slice(); next.splice(index + 1, 0, JSON.parse(JSON.stringify(current))); commit(next, index + 1); };
  const deleteSlide = () => {
    if (slides.length <= 1) { toast.message('A report needs at least one page'); return; }
    const next = slides.filter((_, i) => i !== index);
    commit(next, Math.max(0, index - (index >= next.length ? 1 : 0)));
  };
  const moveSlide = (dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= slides.length) return;
    const next = slides.slice();
    [next[index], next[j]] = [next[j], next[index]];
    commit(next, j);
  };

  const buildWithKai = async () => {
    const prompt = (kaiPrompt.trim() || String(current?.kaiHint || '')).trim();
    if (!prompt || !current) return;
    setGenerating(true);
    try {
      const r = await coworkApi.presentations.generateSlides(presentationId, { prompt, index, slides });
      if (!r.slides.length) throw new Error('Kai returned nothing');
      const next = slides.slice();
      next.splice(index, 1, ...r.slides.map((s) => ({ ...s, kaiHint: undefined })));
      commit(next, index);
      setKaiPrompt('');
      toast.success(r.slides.length === 1 ? 'Page built' : `${r.slides.length} pages built`);
      if (r.note) toast.message(r.note);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  /** Voice-directed Kai call: replace the page at `at`, or insert new pages at `at`. */
  /** Beautify actions: each is one commit, so undo restores the page or the deck settings. Returns a short note. */
  const beautifyAt = async (action: 'design' | 'structure' | 'restyle', opts: { at?: number; brief?: string } = {}): Promise<string> => {
    if (action === 'restyle') {
      const r = await coworkApi.presentations.beautify(presentationId, { action: 'restyle', brief: opts.brief || restyleBrief });
      if (r.settings) setDeck(r.settings);
      return r.note || 'Deck restyled';
    }
    const at = opts.at ?? index; const page = slidesRef.current[at]; if (!page) throw new Error('No such page');
    const r = await coworkApi.presentations.beautify(presentationId, { action, slide: page, brief: opts.brief });
    if (!r.slide) throw new Error('Nothing came back');
    const next = slidesRef.current.slice(); next[at] = r.slide; commit(next, at);
    const note = action === 'design' ? `Designed as ${String(r.composition || 'a composition').replace(/-/g, ' ')}` : `Structured as a ${String(r.slide.type)} page`;
    return r.note ? `${note}. ${r.note}` : note;
  };
  const beautify = async (action: 'design' | 'structure' | 'restyle', brief?: string) => {
    setBeautifying(action); setBeautifyOpen(false);
    try { const note = await beautifyAt(action, { brief }); toast.success(note, { duration: 6000 }); }
    catch (e) { toast.error((e as Error).message); }
    finally { setBeautifying(null); }
  };

  // Keyboard on the selected element: arrows nudge (shift = 5%), backspace/delete removes, cmd/ctrl+D duplicates.
  useEffect(() => {
    if (!selectedEl) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const step = e.shiftKey ? 5 : 1;
      const move = (dx: number, dy: number) => updateCurrent((sl) => {
        const ids = multiEl.length > 1 ? multiEl : [selectedEl];
        const upd = (list: any[]) => list.map((el: any, j: number) => (ids.includes(el?.id || `legacy-${j}`) ? { ...el, x: Math.max(0, Math.min(100 - (Number(el.w) || 0), (Number(el.x) || 0) + dx)), y: Math.max(0, Math.min(100 - (Number(el.h) || 0), (Number(el.y) || 0) + dy)) } : el));
        return { ...sl, ...(Array.isArray(sl.elements) ? { elements: upd(sl.elements) } : {}), ...(Array.isArray(sl.boxes) ? { boxes: upd(sl.boxes) } : {}) };
      });
      if (e.key === 'ArrowLeft') { e.preventDefault(); move(-step, 0); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); move(step, 0); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(0, -step); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); move(0, step); }
      else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); updateCurrent((sl) => ({ ...sl, elements: (Array.isArray(sl.elements) ? sl.elements : []).filter((el: any) => el?.id !== selectedEl), boxes: (Array.isArray(sl.boxes) ? sl.boxes : []).filter((b: any, j: number) => (b?.id || `legacy-${j}`) !== selectedEl) })); setSelectedEl(null); }
      else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') { e.preventDefault(); updateCurrent((sl) => { const src = (Array.isArray(sl.elements) ? sl.elements : []).find((el: any) => el?.id === selectedEl); if (!src) return sl; const copy = { ...src, id: Math.random().toString(36).slice(2, 10), x: Math.min(90, src.x + 3), y: Math.min(90, src.y + 3) }; setTimeout(() => setSelectedEl(copy.id), 0); return { ...sl, elements: [...sl.elements, copy] }; }); }
      else if (e.key === 'Escape') { setSelectedElRaw(null); setMultiEl([]); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEl, multiEl, updateCurrent]);
  useEffect(() => { setSelectedElRaw(null); setMultiEl([]); }, [index]);

  // Load the Google fonts the deck uses (deck font, elements, styled words).
  useDeckFonts(slides, settings.fontFamily);

  const generateAt = async ({ prompt, at, insert, quick, onProgress }: { prompt: string; at: number; insert: boolean; quick?: boolean; onProgress?: (label: string) => void }) => {
    setGenerating(true);
    try {
      const base = slidesRef.current.map((s) => (s._pending ? { ...s, _pending: undefined } : s));
      const progress = (label: string) => { setPendingLabel(label); onProgress?.(label); };
      const r = await coworkApi.presentations.generateSlidesStream(presentationId, { prompt, index: insert ? undefined : at, slides: base, maxSlides: 1, quick: !!quick }, progress);
      if (r.otherWorkspace) throw new Error(r.note || 'Your login belongs to another workspace, so this brand\'s data is not available here.');
      if (!r.slides.length) throw new Error('Kai returned nothing');
      const next = slidesRef.current.slice();
      next.splice(at, insert ? 0 : 1, ...r.slides.map((s) => ({ ...s, kaiHint: undefined })));
      commit(next, at);
      if (r.note) toast.message(r.note);
    } catch (e) { toast.error((e as Error).message); throw e; }
    finally { setGenerating(false); setPendingLabel(''); }
  };

  async function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
  const exportDeck = async (kind: 'pdf' | 'pptx', pptxMode: 'editable' | 'pixel-perfect' = 'editable') => {
    setExporting(kind); setPptxMenu(false);
    try {
      if (dirtyRef.current) await coworkApi.presentations.update(presentationId, { title, slides, settings });
      const safe = title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'report';
      const blob = kind === 'pdf' ? await coworkApi.presentations.exportPdf(presentationId) : await coworkApi.presentations.exportPptx(presentationId, pptxMode);
      await downloadBlob(blob, `${safe}${kind === 'pptx' && pptxMode === 'pixel-perfect' ? '-images' : ''}.${kind}`);
    } catch (e) {
      toast.error(`Export failed: ${(e as Error).message}`);
    } finally {
      setExporting(null);
    }
  };

  const setDeck = (patch: Partial<DeckSettings>) => { dirtyRef.current = true; setSettings((s) => ({ ...s, ...patch })); };
  const uploadLogo = async (file: File) => {
    setUploadingLogo(true);
    try {
      const { url } = await coworkApi.brands.uploadAsset(file, 'logoDark');
      setDeck({ logoUrl: url, showLogo: true });
      toast.success('Logo updated for this report');
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (error) return <div className="p-8 text-[13px] text-[#b42318]">{error}</div>;
  if (!loaded) return <div className="flex h-full items-center justify-center p-20"><Loader2 className="h-5 w-5 animate-spin text-[#667085]" /></div>;

  const kaiPlaceholder = String(current?.kaiHint || KAI_EXAMPLES[index % KAI_EXAMPLES.length]);

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col bg-[#f8f9fc]">
      {picker && (
        <ContentHubPicker
          campaignId={settings.generation?.campaign?.id}
          aiItems={settings.generation?.aiContent?.items}
          onGenerateAi={async () => {
            try {
              const r = await coworkApi.presentations.aiContent(presentationId);
              setSettings((s) => ({ ...s, generation: s.generation ? { ...s.generation, aiContent: { ...(s.generation.aiContent || {}), enabled: true, status: r.status, items: r.all } } : s.generation }));
              if (r.errors?.length && !r.items.length) toast.error(`Could not generate visuals: ${r.errors[0]}`);
              else toast.success(`${r.items.length} AI visual${r.items.length === 1 ? '' : 's'} added`);
            } catch (e) { toast.error((e as Error).message); }
          }}
          onPickAi={(m) => { picker.apply(m); setPicker(null); }}
          initialMode={picker.creator ? 'creators' : undefined}
          onPickCreator={picker.creator ? (f) => { picker.creator!(f); setPicker(null); } : undefined}
          onClose={() => setPicker(null)}
          onPick={(m) => { picker.apply(m); setPicker(null); }}
        />
      )}
      {importFrom && <ImportPagesDialog initial={importFrom} currentId={presentationId} theme={theme} title={title} onClose={() => setImportFrom(null)} onAdd={insertSlides} />}
      {teamPick && <TeamMemberPicker onPick={(m) => { teamPick(m); setTeamPick(null); }} onClose={() => setTeamPick(null)} />}
      {presenting && <PresentationModal presentationId={presentationId} initialIndex={index} onClose={() => setPresenting(false)} />}
      {lykkePresenting && <LykkePresenter presentationId={presentationId} initialIndex={index} mode={lykkePresenting === 'copilot' ? 'copilot' : 'present'} onClose={() => setLykkePresenting(false)} />}

      {/* Top bar */}
      <div className="flex items-center gap-3 border-b border-[#eaecf0] bg-white px-4 py-2.5">
        <button onClick={onBack} className="rounded-md p-1 hover:bg-[#f2f4f7]" aria-label="Back to reports"><ArrowLeft className="h-4 w-4 text-[#667085]" /></button>
        <input
          value={title}
          onChange={(e) => { dirtyRef.current = true; setTitle(e.target.value); }}
          className="min-w-0 flex-1 rounded-md border border-transparent px-2 py-1 text-[15px] font-semibold text-[#101828] hover:border-[#eaecf0] focus:border-[#d0d5dd] focus:outline-none"
          placeholder="Untitled report"
        />
        <span className="text-[11px] text-[#98a2b3]">
          {saveState === 'saved' && <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>}
          {saveState === 'dirty' && 'Unsaved changes'}
          {saveState === 'saving' && <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>}
          {saveState === 'error' && <span className="text-[#b42318]">Not saved</span>}
        </span>
        <button onClick={undo} className={btnCls} disabled={undoStack.current.length === 0} title="Undo"><Undo2 className="h-3.5 w-3.5" /></button>
        <div className="relative">
          <button onClick={() => setBeautifyOpen((o) => !o)} disabled={!!beautifying} className={btnCls} title="Let the agent redesign this page or restyle the whole deck — every change is one undo away">{beautifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Beautify</button>
          {beautifyOpen && (
            <div className="absolute right-0 top-9 z-40 w-[320px] rounded-xl border border-[#eaecf0] bg-white p-3 shadow-2xl">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#667085]">This page</div>
              <button onClick={() => void beautify('design')} className="block w-full rounded-lg px-2 py-1.5 text-left text-[13px] text-[#101828] hover:bg-[#f9fafb]"><span className="font-semibold">Design it</span><span className="block text-[11px] text-[#667085]">A composed layout with the same words and numbers.</span></button>
              <button onClick={() => void beautify('structure')} className="block w-full rounded-lg px-2 py-1.5 text-left text-[13px] text-[#101828] hover:bg-[#f9fafb]"><span className="font-semibold">Structure it</span><span className="block text-[11px] text-[#667085]">Turn a free canvas back into a clean page type.</span></button>
              <div className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wide text-[#667085]">Whole deck</div>
              <div className="flex flex-wrap gap-1">
                {[['beatly-light', 'Beatly light'], ['editorial', 'Editorial'], ['tech', 'Tech'], ['warm', 'Warm'], ['mono', 'Monochrome']].map(([id, label]) => <button key={id} onClick={() => void beautify('restyle', id)} className="rounded-full border border-[#d0d5dd] px-2 py-0.5 text-[11px] text-[#344054] hover:bg-[#f9fafb]">{label}</button>)}
              </div>
              <div className="mt-2 flex gap-1">
                <input value={restyleBrief} onChange={(e) => setRestyleBrief(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && restyleBrief.trim()) void beautify('restyle'); }} placeholder="or describe a style: warmer, editorial, more contrast…" className="min-w-0 flex-1 rounded-lg border border-[#d0d5dd] px-2 py-1 text-[12px] focus:border-[#be43ff] focus:outline-none" />
                <button onClick={() => void beautify('restyle')} disabled={!restyleBrief.trim()} className={btnPrimaryCls}>Go</button>
              </div>
              <div className="mt-2 text-[10px] text-[#98a2b3]">Fonts, palette and page colours change; page content does not. Undo reverts.</div>
            </div>
          )}
        </div>
        <button onClick={() => setVoice((v) => !v)} className={voice ? 'flex items-center gap-1.5 rounded-lg bg-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-white' : btnCls} title="Lykke at the keyboard: talk, and the deck changes — go to, move, delete, layout, theme, new pages, rewrites"><Mic className="h-3.5 w-3.5" /> Build with Lykke</button>
        <button onClick={() => setPresenting(true)} className={btnCls} title="Present from this page"><Play className="h-3.5 w-3.5" /> Present</button>
        <button onClick={() => setVideosMuted(!videosMuted)} className={btnCls} title={videosMuted ? 'Videos are muted everywhere (editor, present mode, shared links) — click to unmute' : 'Mute all videos, remembered on this device'}>{videosMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}{videosMuted ? ' Muted' : ''}</button>
        <button onClick={() => setLykkePresenting('present')} title="Lykke walks the listener through the deck on a live voice call — ask her anything, she jumps to any page" className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#be43ff] to-[#7c3aed] px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:from-[#a835e0] hover:to-[#6d28d9]"><Phone className="h-3.5 w-3.5" /> Ask Lykke to present</button>
        <button onClick={() => setLykkePresenting('copilot')} title="You present; Lykke sits in, follows the room and the pages, takes notes into the wiki, and answers when someone says “Lykke, …”" className="flex items-center gap-1.5 rounded-lg border border-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-[#7c3aed] hover:bg-[#fcf7ff]"><Ear className="h-3.5 w-3.5" /> Present with Lykke</button>
        <VisibilityControl value={vis} onChange={changeVisibility} canEdit={vis.isOwner} saving={savingVis} unowned={vis.unowned} onClaim={async () => { try { await coworkApi.presentations.claim(presentationId); setVis((v) => ({ ...v, isOwner: true, unowned: false })); void queryClient.invalidateQueries({ queryKey: ['cowork', 'presentations'] }); toast.success('You are now the owner of this report'); } catch (e) { toast.error((e as Error).message); } }} />
        <ShareLinkControl kind="report" id={presentationId} shareEnabled={share.shareEnabled} shareToken={share.shareToken} canEdit={vis.isOwner} onChanged={(v) => { setShare(v); void queryClient.invalidateQueries({ queryKey: ['cowork', 'presentations'] }); }} />
        <DeckGuardrails presentationId={presentationId} slidesVersion={slides} onGoToPage={(page) => setIndex(Math.max(0, Math.min(page - 1, slides.length - 1)))} onBeforeCheck={async () => { if (dirtyRef.current) await coworkApi.presentations.update(presentationId, { title, slides, settings }); }} buttonClassName={btnCls} />
        <button onClick={() => exportDeck('pdf')} disabled={!!exporting} className={btnCls}>{exporting === 'pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF</button>
        <div className="relative">
          <button onClick={() => setPptxMenu((o) => !o)} disabled={!!exporting} className={btnCls} title="Download as PowerPoint">{exporting === 'pptx' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PPTX <ChevronDown className="h-3 w-3" /></button>
          {pptxMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setPptxMenu(false)} />
              <div className="absolute right-0 top-9 z-20 w-72 rounded-xl border border-[#eaecf0] bg-white p-1.5 shadow-xl">
                <button onClick={() => exportDeck('pptx', 'editable')} className="block w-full rounded-lg px-3 py-2 text-left hover:bg-[#f9f5ff]">
                  <span className="block text-[12.5px] font-semibold text-[#101828]">Editable text</span>
                  <span className="block text-[11px] text-[#667085]">Real text, tables and charts you can edit in Canva, PowerPoint or Keynote. Simpler layout.</span>
                </button>
                <button onClick={() => exportDeck('pptx', 'pixel-perfect')} className="block w-full rounded-lg px-3 py-2 text-left hover:bg-[#f9f5ff]">
                  <span className="block text-[12.5px] font-semibold text-[#101828]">Pixel-perfect images</span>
                  <span className="block text-[11px] text-[#667085]">Each page as a high-resolution image, exactly as on screen. Not editable.</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div id="deck-guardrails-slot" />

      <div className="flex min-h-0 flex-1">
        {/* Slide list */}
        <div className="flex w-52 shrink-0 flex-col border-r border-[#eaecf0] bg-white">
          <div className="flex-1 overflow-y-auto p-3">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`mb-2 block w-full overflow-hidden rounded-lg border text-left ${i === index ? 'border-[#be43ff] ring-2 ring-[#be43ff]/20' : isGhost(s) ? 'border-dashed border-[#c9b3e6] hover:border-[#be43ff]' : 'border-[#eaecf0] hover:border-[#d0d5dd]'}`}
              >
                <div className={isGhost(s) ? 'opacity-55 saturate-50' : undefined}><SlideThumb slide={s} index={i} theme={theme} title={title} /></div>
                <div className="flex items-center justify-between px-2 py-1 text-[10px] text-[#667085]">
                  <span>{i + 1}</span>
                  {isGhost(s)
                    ? <span className="truncate font-semibold text-[#7a1fbf]">{s._plan.building ? 'Writing…' : 'Planned'}</span>
                    : <span className={`truncate ${s.type === 'free' ? 'font-semibold text-[#be43ff]' : ''}`}>{BASE_TYPES.find((t) => t.id === (s.type || 'content'))?.label || s.type}</span>}
                </div>
              </button>
            ))}
          </div>
          <div className="relative border-t border-[#eaecf0] p-2">
            <button onClick={() => setAddMenu((o) => !o)} className={`${btnPrimaryCls} w-full justify-center`}><Plus className="h-3.5 w-3.5" /> Add page</button>
            {addMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setAddMenu(false)} />
                <div className="absolute bottom-12 left-2 z-50 max-h-[78vh] w-[44rem] overflow-y-auto rounded-xl border border-[#eaecf0] bg-white p-3 shadow-xl">
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    <button onClick={() => { setAddMenu(false); setImportFrom('deck'); }} className="flex items-center gap-2.5 rounded-lg border border-[#eaecf0] px-3 py-2.5 text-left hover:border-[#be43ff] hover:bg-[#fcf7ff]">
                      <Copy className="h-4 w-4 shrink-0 text-[#be43ff]" />
                      <span><span className="block text-[12.5px] font-semibold text-[#101828]">From another report</span><span className="block text-[11px] text-[#667085]">Copy pages out of any report in this workspace</span></span>
                    </button>
                    <button onClick={() => { setAddMenu(false); setImportFrom('file'); }} className="flex items-center gap-2.5 rounded-lg border border-[#eaecf0] px-3 py-2.5 text-left hover:border-[#be43ff] hover:bg-[#fcf7ff]">
                      <Upload className="h-4 w-4 shrink-0 text-[#be43ff]" />
                      <span><span className="block text-[12.5px] font-semibold text-[#101828]">From a file</span><span className="block text-[11px] text-[#667085]">PowerPoint, PDF or pictures</span></span>
                    </button>
                  </div>
                  {LAYOUT_CATEGORIES.map((cat) => (
                    <div key={cat} className="mb-3">
                      <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#667085]">{cat}</div>
                      <div className="grid grid-cols-4 gap-2">
                        {LAYOUT_CATALOG.filter((l) => l.category === cat).map((l) => (
                          <button key={l.id} onClick={() => addSlide(l.make())} className="group rounded-lg border border-[#eaecf0] p-1.5 text-left hover:border-[#be43ff] hover:bg-[#fcf7ff]" title={l.label}>
                            <SlideThumb slide={l.preview()} index={l.id === 'cover' ? 0 : 1} theme={theme} title={title} radius={6} />
                            <div className="mt-1 truncate text-[11px] font-medium text-[#101828]">{l.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stage — editable canvas */}
        <div className="flex min-w-0 flex-1 flex-col items-center overflow-y-auto p-6">
          <div className="w-full max-w-4xl">
            {voice && <DeckCopilot presentationId={presentationId} theme={theme} slides={slides} index={index} setIndex={setIndex} commit={commit} undo={undo} onGenerate={generateAt} onBeautify={beautifyAt} onClose={() => setVoice(false)}
              pendingBrief={settings.pendingBrief} pendingMode={settings.pendingMode} pendingAutoBuild={settings.pendingAutoBuild} onPendingBriefUsed={() => setDeck({ pendingBrief: undefined, pendingMode: undefined, pendingAutoBuild: undefined })} buildRequest={buildReq} />}
            <div className="mb-2 flex items-center justify-between gap-2 text-[11px] text-[#667085]">
              <span className="flex shrink-0 items-center gap-2">
                Page {index + 1} of {slides.length}
                {/* Page mode lives up here, off the canvas: an unlocked chip on Free Style pages, a locked · Unlock button on template pages. */}
                {current && !deckGenerating && (current.type === 'free'
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-[#f4e6ff] px-2 py-0.5 text-[10px] font-semibold text-[#7a1fbf]" title="Free Style: the layout is unlocked — everything on this page is a movable element"><Unlock className="h-3 w-3" /> Layout unlocked</span>
                  : <button onClick={takeOver} className="inline-flex items-center gap-1 rounded-full border border-[#d0d5dd] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#344054] hover:border-[#be43ff] hover:text-[#7a1fbf]" title="This is a template page: the layout is fixed. Unlock it to move, resize and restyle everything (undo brings the template back)"><Lock className="h-3 w-3" /> Layout locked · Unlock</button>)}
              </span>
              {current && !deckGenerating && <ElementToolbox slide={current} update={(m) => updateCurrent(m)} theme={theme} onSelect={setSelectedEl} stageRef={stageRef} />}
              <div className="flex items-center gap-1">
                <button onClick={() => moveSlide(-1)} disabled={index === 0} className={btnCls} title="Move up"><ChevronUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => moveSlide(1)} disabled={index >= slides.length - 1} className={btnCls} title="Move down"><ChevronDown className="h-3.5 w-3.5" /></button>
                <button onClick={duplicateSlide} className={btnCls} title="Duplicate page"><Copy className="h-3.5 w-3.5" /></button>
                <button onClick={deleteSlide} className={`${btnCls} text-[#b42318]`} title="Delete page"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div ref={stageRef} data-editable={!deckGenerating ? '' : undefined} style={{ position: 'relative', aspectRatio: '16 / 9', containerType: 'size', background: theme.bg, borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(16,24,40,0.15)' } as CSSProperties}>
              {current && (
                <SlideEditProvider api={{ ...editApi, editable: !deckGenerating }}>
                  <ElementSelection.Provider value={{ selected: selectedEl, select: setSelectedEl, multi: multiEl }}>
                    <SlideRenderer slide={current} index={index} theme={theme} title={title} />
                  </ElementSelection.Provider>
                </SlideEditProvider>
              )}
              {current && isGhost(current) && !current._pending && !deckGenerating && (
                <div className="pointer-events-none absolute inset-0 rounded-2xl" style={{ boxShadow: 'inset 0 0 0 0.35cqw #be43ff', background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(190,67,255,0.05))' }}>
                  <div className="absolute left-[5cqw] top-[3cqh] rounded-full bg-[#be43ff] px-[1cqw] py-[0.6cqh] text-[1cqw] font-bold uppercase tracking-wider text-white">Planned · not written yet</div>
                </div>
              )}
              {current?._pending && !deckGenerating && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/80 text-center backdrop-blur-[2px]">
                  <Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" />
                  <div className="text-[15px] font-semibold text-[#101828]">{pendingLabel || String(current._pending)}</div>
                  <div className="text-[12px] text-[#667085]">Keep talking — you can move around the deck meanwhile.</div>
                </div>
              )}
              {deckGenerating && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/85 text-center backdrop-blur-[2px]">
                  <Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" />
                  <div className="text-[15px] font-semibold text-[#101828]">Kai is building your report</div>
                  <div className="max-w-md px-6 text-[12px] text-[#667085]">Planning the pages, pulling live campaign data and Content Hub media, writing every page. Usually 1–3 minutes — you can leave and come back.</div>
                  {settings.generation?.brief && <div className="max-w-md truncate px-6 text-[11px] italic text-[#98a2b3]">“{settings.generation.brief}”</div>}
                </div>
              )}
              {settings.generation?.status === 'failed' && (
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-[#fef3f2] px-4 py-2 text-[12px] text-[#b42318]">
                  <span className="truncate">Kai couldn’t build this report: {settings.generation.error || 'unknown error'}</span>
                  <span className="flex shrink-0 gap-3">
                    <button onClick={async () => { try { const r = await coworkApi.presentations.regenerate(presentationId); setSettings((s) => ({ ...s, generation: r.generation })); } catch (e) { toast.error((e as Error).message); } }} className="font-semibold underline">Retry build</button>
                    <button onClick={() => setDeck({ generation: undefined })} className="font-semibold underline">Dismiss</button>
                  </span>
                </div>
              )}
            </div>
            {current && isGhost(current) && !current._pending && !deckGenerating && (
              <div className="mt-3 rounded-xl border border-dashed border-[#be43ff]/60 bg-white p-4 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[#7a1fbf]">What this page will say</div>
                <textarea value={String(current._plan.description || '')} onChange={(e) => updateCurrent((s) => ({ ...s, _plan: { ...s._plan, description: e.target.value } }))} rows={3} placeholder="The brief this page is written from — say it the way an analyst briefs a designer." className="mt-1 w-full resize-none bg-transparent text-[15px] leading-snug text-[#101828] focus:outline-none" />
                <textarea value={String(current._plan.notes || '')} onChange={(e) => updateCurrent((s) => ({ ...s, _plan: { ...s._plan, notes: e.target.value } }))} rows={1} placeholder="Your notes (optional): numbers to use, tone, what to avoid…" className="mt-1 w-full resize-none bg-transparent text-[13px] text-[#667085] focus:outline-none" />
                <div className="mt-2 flex items-center gap-4 text-[12px] text-[#344054]">
                  <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!current._plan.elements?.chart} onChange={(e) => updateCurrent((s) => ({ ...s, _plan: { ...s._plan, elements: { text: true, image: false, table: false, ...(s._plan.elements || {}), chart: e.target.checked } } }))} className="accent-[#be43ff]" /> Chart</label>
                  <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!current._plan.elements?.image} onChange={(e) => updateCurrent((s) => ({ ...s, _plan: { ...s._plan, elements: { text: true, chart: false, table: false, ...(s._plan.elements || {}), image: e.target.checked } } }))} className="accent-[#be43ff]" /> Image</label>
                  <label className="flex items-center gap-1.5" title="Campaign numbers, creators, posts or audience from the datalake"><input type="checkbox" checked={!!current._plan.needsData} onChange={(e) => updateCurrent((s) => ({ ...s, _plan: { ...s._plan, needsData: e.target.checked } }))} className="accent-[#be43ff]" /> Live data</label>
                  <span className="ml-auto text-[11px] text-[#98a2b3]">Title, layout and order: edit them like any page.</span>
                  {ghostCount > 1 && <button onClick={() => buildGhost('all')} disabled={!!current._plan.building} className={btnCls}>Build all {ghostCount}</button>}
                  <button onClick={() => buildGhost(index)} disabled={!!current._plan.building} className={btnPrimaryCls}>{current._plan.building ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Build this page</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Inspector — only what the canvas can't do */}
        <div className="flex w-[20rem] shrink-0 flex-col overflow-y-auto border-l border-[#eaecf0] bg-white">
          <div className="border-b border-[#eaecf0] p-4">
            {current && isGhost(current) ? (
              <>
                <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-[#101828]"><Sparkles className="h-3.5 w-3.5 text-[#be43ff]" /> This page's brief</div>
                <textarea value={String(current._plan.description || '')} onChange={(e) => updateCurrent((s) => ({ ...s, _plan: { ...s._plan, description: e.target.value } }))} rows={4} placeholder="What this page shows and says" className={`${inputCls} resize-none`} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') buildGhost(index); }} />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-[#98a2b3]">{ghostCount > 1 ? `${ghostCount} pages planned.` : 'Planned, not written yet.'}</span>
                  <div className="flex items-center gap-1.5">
                    {ghostCount > 1 && <button onClick={() => buildGhost('all')} disabled={!!current._plan.building} className={btnCls}>Build all {ghostCount}</button>}
                    <button onClick={() => buildGhost(index)} disabled={!!current._plan.building} className={btnPrimaryCls}>{current._plan.building ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Build</button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-[#101828]"><Sparkles className="h-3.5 w-3.5 text-[#00b1a5]" /> Rewrite this page</div>
                <textarea
                  value={kaiPrompt}
                  onChange={(e) => setKaiPrompt(e.target.value)}
                  rows={3}
                  placeholder={kaiPlaceholder}
                  className={`${inputCls} resize-none`}
                  onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') buildWithKai(); }}
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-[#98a2b3]">{current?.kaiHint && !kaiPrompt.trim() ? 'Press Build to use the suggested brief.' : 'Live campaign data + Content Hub. Replaces this page.'}</span>
                  <button onClick={buildWithKai} disabled={generating || (!kaiPrompt.trim() && !current?.kaiHint)} className={btnPrimaryCls}>
                    {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Build
                  </button>
                </div>
              </>
            )}
          </div>

          {current && (
            <>
            {multiEl.length > 1 && <AlignBar slide={current} ids={multiEl} update={(m) => updateCurrent(m)} />}
            {!selectedEl && (current.type === 'chart' || (!current.type && current.chart)) && current.variant !== 'overview' && <ChartPageEditor slide={current} update={(m) => updateCurrent(m)} theme={theme} />}
            {selectedEl && multiEl.length <= 1 && <ElementInspector slide={current} selectedId={selectedEl} update={(m) => updateCurrent(m)} onSelect={(id) => setSelectedEl(id)} theme={theme} />}
            <div className="space-y-4 border-b border-[#eaecf0] p-4">
              <div>
                <label className={labelCls}>Page type</label>
                <PageTypePicker slide={current} theme={theme} title={title} onPick={(l) => updateCurrent((s) => (isGhost(s) ? reshapeGhost(s, l.id) : retype(s, l.make())))} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Background</label>
                  <select value={current.theme || 'light'} onChange={(e) => updateCurrent({ theme: e.target.value })} className={inputCls}>
                    {THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {['content', 'headline', 'title', 'closing'].includes(current.type || 'content') && (
                  <div>
                    <label className={labelCls}>Layout</label>
                    <select value={current.layout || 'left'} onChange={(e) => updateCurrent({ layout: e.target.value })} className={inputCls}>
                      {LAYOUTS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>Page colours</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['bgColor', 'textColor'] as const).map((key) => {
                    const val = typeof current[key] === 'string' ? String(current[key]) : '';
                    const fallback = key === 'bgColor' ? theme.bg : theme.text;
                    return (
                      <div key={key} className="flex items-center gap-1.5">
                        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(val) ? val : (/^#[0-9a-fA-F]{6}$/.test(fallback) ? fallback : '#ffffff')} onChange={(e) => updateCurrent({ [key]: e.target.value })} className="h-8 w-9 shrink-0 cursor-pointer rounded border border-[#eaecf0]" title={key === 'bgColor' ? 'Background' : 'Text'} />
                        <span className="min-w-0 flex-1 truncate text-[11px] text-[#667085]">{key === 'bgColor' ? 'Background' : 'Text'}</span>
                        {val && <button onClick={() => updateCurrent({ [key]: undefined })} className={btnCls} title="Back to theme colour"><X className="h-3 w-3" /></button>}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1 text-[10px] text-[#98a2b3]">This page only. Deck-wide colours live under Brand &amp; logo.</div>
              </div>
              {current.type === 'showcase' && CREATOR_VARIANTS.includes(String(current.variant)) && (
                <div>
                  <label className={labelCls}>Creator fields</label>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                    {CREATOR_FIELDS.map((f) => {
                      const on = creatorFieldsOf(current)[f.id];
                      return (
                        <label key={f.id} className="flex cursor-pointer items-center gap-1.5 text-[11.5px] text-[#344054]">
                          <input type="checkbox" checked={on} onChange={(e) => updateCurrent((s) => ({ ...s, fields: { ...creatorFieldsOf(s), [f.id]: e.target.checked } }))} className="h-3.5 w-3.5 accent-[#be43ff]" />
                          <span className="truncate" title={f.label}>{f.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-1 text-[10px] text-[#98a2b3]">Cards added from the creator database show these facts; hand-written lines always stay.</div>
                </div>
              )}
            </div>
            </>
          )}

          {/* Brand & logo — deck level */}
          <div className="p-4">
            <button onClick={() => setBrandOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
              <span className="text-[12px] font-semibold text-[#101828]">Brand & logo</span>
              <ChevronRight className={`h-3.5 w-3.5 text-[#98a2b3] transition ${brandOpen ? 'rotate-90' : ''}`} />
            </button>
            <div className="mt-1 text-[10px] text-[#98a2b3]">Whole report. Defaults to your Brand OS; changes here apply to this report only.</div>
            {brandOpen && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className={labelCls}>Logo</label>
                  <div className="flex items-center gap-2 rounded-lg border border-[#eaecf0] p-2">
                    <div className="grid h-12 w-20 shrink-0 place-items-center overflow-hidden rounded bg-[#f2f4f7]">
                      {theme.logoUrl ? <img src={theme.logoUrl} alt="" className="max-h-10 max-w-[4.5rem] object-contain" /> : <span className="text-[10px] text-[#98a2b3]">No logo</span>}
                    </div>
                    <div className="flex flex-1 flex-wrap gap-1.5">
                      <button onClick={() => logoInput.current?.click()} disabled={uploadingLogo} className={btnCls}>{uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload</button>
                      <button onClick={() => pickMedia((m) => setDeck({ logoUrl: m.posterUrl || m.url, showLogo: true }))} className={btnCls}><ImagePlus className="h-3.5 w-3.5" /> Content Hub</button>
                      {(theme.logoUrl || settings.logoUrl) && (
                        <button onClick={() => setDeck(settings.logoUrl ? { logoUrl: null, showLogo: true } : { showLogo: false })} className={`${btnCls} text-[#b42318]`} title={settings.logoUrl ? 'Back to Brand OS logo' : 'Hide logo on this report'}><X className="h-3.5 w-3.5" /> {settings.logoUrl ? 'Reset' : 'Hide'}</button>
                      )}
                      {settings.showLogo === false && <button onClick={() => setDeck({ showLogo: true })} className={btnCls}>Show logo</button>}
                    </div>
                    <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.currentTarget.value = ''; }} />
                  </div>
                </div>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-[#eaecf0] px-2.5 py-2">
                  <span>
                    <span className="block text-[12px] font-medium text-[#101828]">Beatly watermark</span>
                    <span className="block text-[10px] text-[#98a2b3]">Small wordmark top-right of every page</span>
                  </span>
                  <input type="checkbox" checked={settings.beatlyMark !== false} onChange={(e) => setDeck({ beatlyMark: e.target.checked })} className="accent-[#be43ff]" />
                </label>
                <div>
                  <label className={labelCls}>Brand name</label>
                  <input value={settings.brandName ?? ''} onChange={(e) => setDeck({ brandName: e.target.value || null })} placeholder={brand?.brand?.name || 'Shown as the brand mark'} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Primary colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings.primaryColor || theme.primary} onChange={(e) => setDeck({ primaryColor: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-[#d0d5dd] bg-white p-0.5" />
                    <input value={settings.primaryColor ?? ''} onChange={(e) => setDeck({ primaryColor: e.target.value || null })} placeholder={theme.primary} className={inputCls} />
                    {settings.primaryColor && <button onClick={() => setDeck({ primaryColor: null })} className={btnCls} title="Back to Brand OS colour"><X className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Font</label>
                  <select value={settings.fontFamily ?? ''} onChange={(e) => setDeck({ fontFamily: e.target.value || null })} className={inputCls}>
                    <option value="">Brand OS font{brand?.brand?.fonts?.[0] ? ` (${brand.brand.fonts[0]})` : ''}</option>
                    {FONT_CHOICES.map((f) => <option key={f.family} value={f.family}>{f.label} · {f.kind}</option>)}
                  </select>
                  <div className="mt-1 text-[10px] text-[#98a2b3]">Every page. Elements can still pick their own font.</div>
                </div>
                <div>
                  <label className={labelCls}>Palette (charts, shapes)</label>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(settings.palette || []).map((c, i) => (
                      <label key={i} className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-md border border-[#d0d5dd]" style={{ background: c }} title={c}>
                        <input type="color" value={c} onChange={(e) => setDeck({ palette: (settings.palette || []).map((x, j) => (j === i ? e.target.value : x)) })} className="absolute inset-0 opacity-0" />
                        <button onClick={(e) => { e.preventDefault(); setDeck({ palette: (settings.palette || []).filter((_, j) => j !== i) }); }} className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-white text-[9px] leading-none text-[#b42318] shadow" title="Remove">×</button>
                      </label>
                    ))}
                    {(settings.palette || []).length < 8 && <button onClick={() => setDeck({ palette: [...(settings.palette || []), (settings.palette || []).length === 0 ? theme.primary : (settings.palette || []).length === 1 ? theme.accent : '#F59E0B'] })} className={btnCls} title="Add a colour">+</button>}
                    {(settings.palette || []).length > 0 && <button onClick={() => setDeck({ palette: null })} className={btnCls} title="Back to the brand palette"><X className="h-3.5 w-3.5" /></button>}
                  </div>
                  <div className="mt-1 text-[10px] text-[#98a2b3]">Series and fills follow this order. Empty = brand colours.</div>
                </div>
                {(['backgroundColor', 'textColor'] as const).map((key) => (
                  <div key={key}>
                    <label className={labelCls}>{key === 'backgroundColor' ? 'Page background' : 'Text colour'}</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={settings[key] || (key === 'backgroundColor' ? theme.bg : theme.text)} onChange={(e) => setDeck({ [key]: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-[#eaecf0]" />
                      <input value={settings[key] ?? ''} onChange={(e) => setDeck({ [key]: e.target.value || null })} placeholder={key === 'backgroundColor' ? theme.bg : theme.text} className={inputCls} />
                      {settings[key] && <button onClick={() => setDeck({ [key]: null })} className={btnCls} title="Back to the default"><X className="h-3.5 w-3.5" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
